import React from 'react';
import {
  currencyUI,
  currencySchema,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  radioUI,
  radioSchema,
  textUI,
  textSchema,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import VaComboBoxField from 'platform/forms-system/src/js/web-component-fields/VaComboBoxField';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import { getArrayUrlSearchParams } from '~/platform/forms-system/src/js/patterns/array-builder/helpers';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  careFrequencyLabels,
  recipientTypeLabels,
  medicalExpenseTypeLabels,
} from '../../../utils/labels';
import { formatCurrency } from '../../../utils/helpers';
import { transformDate } from './helpers';
import ExpenseCard from '../../../components/ExpenseCard';
import content from '../../../utils/content.json';

const nounSingular = 'medical expense';
const nounPlural = 'medical expenses';

function introDescription() {
  return (
    <div>
      <p className="vads-u-margin-top--0">
        Next we’ll ask about medical or certain other expenses that aren’t
        reimbursed.
      </p>
      <p>These types of expenses can include:</p>
      <ul>
        <li>
          <span className="vads-u-font-weight--bold">
            Recurring medical expenses
          </span>{' '}
          that insurance doesn’t cover
        </li>
        <li>
          <span className="vads-u-font-weight--bold">
            One-time medical expenses
          </span>{' '}
          that insurance doesn’t cover that occurred after you started this form
          or after you submitted an Intent to File
        </li>
      </ul>
      <va-additional-info trigger="How to report monthly recurring expenses">
        <p>
          For recurring monthly expenses, report them as one expense. Include
          the start date and how much you pay each month or year.
        </p>
        <p>Start a new expense if:</p>
        <ul>
          <li>The amount you pay changed</li>
          <li>The expense ended (list as separate, individual expenses)</li>
        </ul>
        <p>
          <strong>Note: </strong>
          Prescription medications are generally not considered recurring.
        </p>
      </va-additional-info>
    </div>
  );
}

function checkIsItemIncomplete(item) {
  return (
    !item?.recipient ||
    ((item.recipient === 'CHILD' || item.recipient === 'OTHER') &&
      !item?.fullNameRecipient) ||
    !item?.paymentDate ||
    !Object.keys(medicalExpenseTypeLabels).includes(item?.purpose) ||
    !item?.paymentFrequency ||
    !item?.provider ||
    !item?.paymentAmount
  );
}

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'medicalExpenses',
  nounSingular,
  nounPlural,
  required: false,
  isItemIncomplete: item => checkIsItemIncomplete(item),
  maxItems: 14,
  text: {
    getItemName: (item, index) =>
      replaceStrValues(content['array-item-name'], [
        index + 1,
        item?.provider || 'Provider',
      ]),
    cardDescription: item => {
      if (!item?.paymentDate || !item?.paymentAmount || !item?.paymentFrequency)
        return null; // this prevents proptype console errors when navigating backwards when there are no items
      return (
        <ExpenseCard
          date={transformDate(item?.paymentDate)}
          amount={formatCurrency(item?.paymentAmount)}
          frequency={careFrequencyLabels?.[item?.paymentFrequency]}
        />
      );
    },
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      nounSingular,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      nounSingular,
    ),
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      nounPlural,
      nounSingular,
    ]),
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(content['array-delete-title'], nounSingular),
    deleteYes: content['array-delete-yes'],
  },
};

/** @returns {PageSchema} */
const introPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['medical-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

/**
 * This page is skipped on the first loop for required flow
 * Cards are populated on this page above the uiSchema if items are present
 *
 * @returns {PageSchema}
 */
const summaryPage = {
  uiSchema: {
    'view:medicalExpensesList': arrayBuilderYesNoUI(
      options,
      { hint: '' },
      { hint: '' },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:medicalExpensesList': arrayBuilderYesNoSchema,
    },
    required: ['view:medicalExpensesList'],
  },
};

/** @returns {PageSchema} */
const recipientPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['medical-recipient-page-title'],
      () => {
        const search = getArrayUrlSearchParams();
        const isEdit = search.get('edit');
        if (isEdit) {
          return content['medical-recipient-edit-description'];
        }
        return null;
      },
    ),
    recipient: radioUI({
      title: content['medical-recipient-question'],
      labels: recipientTypeLabels,
    }),
    fullNameRecipient: textUI({
      title: content['medical-recipient-fullname'],
      expandUnder: 'recipient',
      expandUnderCondition: field => field === 'CHILD' || field === 'OTHER',
      required: (formData, index, fullData) => {
        // Adding a check for formData and fullData since formData is sometimes undefined on load
        // and we can't rely on fullData for testing
        const medicalExpenses =
          formData?.medicalExpenses ?? fullData?.medicalExpenses;
        const medicalExpense = medicalExpenses?.[index];
        return ['CHILD', 'OTHER'].includes(medicalExpense?.recipient);
      },
    }),
    provider: textUI({
      title: content['medical-provider-question'],
      hint: content['medical-provider-hint'],
    }),
  },
  schema: {
    type: 'object',
    properties: {
      recipient: radioSchema(Object.keys(recipientTypeLabels)),
      fullNameRecipient: textSchema,
      provider: textSchema,
    },
    required: ['recipient', 'provider'],
  },
};

/** @returns {PageSchema} */
const purposePage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['medical-purpose-page-title'],
    ),
    purpose: {
      'ui:title': content['medical-purpose-question'],
      'ui:webComponentField': VaComboBoxField,
      'ui:options': {
        labels: medicalExpenseTypeLabels,
      },
      'ui:confirmationField': ({ formData }) => ({
        data: formData.label,
        label: content['medical-purpose-question'],
      }),
    },
    paymentDate: currentOrPastDateUI({
      title: content['medical-payment-date'],
      monthSelect: false,
    }),
  },
  schema: {
    type: 'object',
    properties: {
      purpose: {
        type: 'string',
        enum: Object.keys(medicalExpenseTypeLabels),
      },
      paymentDate: currentOrPastDateSchema,
    },
    required: ['purpose', 'paymentDate'],
  },
};

/** @returns {PageSchema} */
const frequencyPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['medical-frequency-page-title'],
    ),
    paymentFrequency: radioUI({
      title: content['medical-frequency-question'],
      labels: careFrequencyLabels,
    }),
    paymentAmount: currencyUI({
      title: content['medical-payment-amount'],
      max: 999999.99,
    }),
  },
  schema: {
    type: 'object',
    properties: {
      paymentFrequency: radioSchema(Object.keys(careFrequencyLabels)),
      paymentAmount: currencySchema,
    },
    required: ['paymentFrequency', 'paymentAmount'],
  },
};

export const medicalExpensesPages = arrayBuilderPages(options, pageBuilder => ({
  medicalExpensesIntro: pageBuilder.introPage({
    title: content['medical-intro-title'],
    path: 'expenses/medical',
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  }),
  medicalExpensesSummary: pageBuilder.summaryPage({
    title: content['medical-intro-title'],
    path: 'expenses/medical/add',
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  medicalExpensesRecipientPage: pageBuilder.itemPage({
    title: 'Medical recipient',
    path: 'expenses/medical/:index/recipient',
    uiSchema: recipientPage.uiSchema,
    schema: recipientPage.schema,
  }),
  medicalExpensesPurposePage: pageBuilder.itemPage({
    title: 'Expense recipient and purpose',
    path: 'expenses/medical/:index/purpose',
    uiSchema: purposePage.uiSchema,
    schema: purposePage.schema,
  }),
  medicalExpensesFrequencyPage: pageBuilder.itemPage({
    title: content['medical-frequency-page-title'],
    path: 'expenses/medical/:index/frequency',
    uiSchema: frequencyPage.uiSchema,
    schema: frequencyPage.schema,
  }),
}));
